// src/components/modules/devtools/hooks/devIdeFileEditorHandlers.js

const createFileEditorHandlers = (deps) => {
  const {
    currentUsername,
    selectedProjectId,

    selectedFileId,
    setSelectedFileId,
    selectedFileMeta,
    setSelectedFileMeta,
    fileContent,
    setFileContent,
    fileOriginalContent,
    setFileOriginalContent,
    setFileLoading,
    setFileError,
    gptPasteContent,
    setGptPasteContent, // not used yet but kept for future patch friendliness
    changeSummary,
    setChangeSummary,
    setSaveLoading,
    setSaveMessage,
    setDiffText,
    setDiffError,
    setDiffLineCount,
    setFileHistory,
    setFileHistoryError,
    setDiffLoading,
    activeChange,
    loadChangeFiles,
    loadSqlFiles,
    // NEW (optional): used to drive the error popup
    setSaveErrorMessage,
    setSaveErrorOpen,
  } = deps;

  const copyTextToClipboard = async (text) => {
    // Prefer the async Clipboard API when available (usually requires a secure context).
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function" &&
      typeof window !== "undefined" &&
      window.isSecureContext
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for HTTP / older browsers: hidden textarea + execCommand.
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-9999px";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return !!ok;
    } catch {
      return false;
    }
  };

  const handleSelectFile = async (fileId) => {
    setSelectedFileId(fileId);
    setSelectedFileMeta(null);
    setFileContent("");
    setFileOriginalContent("");
    setFileLoading(true);
    setFileError(null);
    setSaveMessage(null);
    setGptPasteContent("");
    setChangeSummary("");

    setDiffText("");
    setDiffError(null);
    setDiffLineCount(null);

    setFileHistory([]);
    setFileHistoryError(null);

    try {
      const params = new URLSearchParams();
      params.set("file_id", String(fileId));
      if (selectedProjectId) {
        params.set("project_id", selectedProjectId);
      }

      const res = await fetch(`/api/dev-ide/file-content?${params.toString()}`);
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to load file.");
      }
      const file = data.file;
      setSelectedFileMeta(file);
      setFileContent(file.content || "");
      setFileOriginalContent(file.content || "");
    } catch (err) {
      setFileError(err.message);
    } finally {
      setFileLoading(false);
    }
  };

  const handleCopyFullForGPT = async () => {
    if (!selectedFileMeta) return;
    const header = `// FILE: ${selectedFileMeta.path}\n// MODULE: ${selectedFileMeta.module}\n\n`;
    const text = header + (fileContent || "");
    try {
      const ok = await copyTextToClipboard(text);
      setSaveMessage(
        ok
          ? "Full file copied for GPT."
          : "Could not copy to clipboard (browser blocked it). Please copy manually."
      );
    } catch {
      setSaveMessage("Could not copy to clipboard. Please copy manually.");
    }
  };

  const handleCopySelectionForGPT = () => {
    // For now this just copies the full file. We can enhance later if needed.
    handleCopyFullForGPT();
  };

  const handleApplyGptPaste = () => {
    if (!gptPasteContent || !gptPasteContent.trim()) {
      setSaveMessage("Paste the updated file content from GPT first.");
      return;
    }
    setFileContent(gptPasteContent);
    setSaveMessage(
      "GPT content applied to editor. Review and then Save to sandbox."
    );
    setDiffText("");
    setDiffError(null);
    setDiffLineCount(null);
  };

  const handlePreviewDiff = async () => {
    if (!selectedFileId || !selectedFileMeta) {
      setDiffError("Select a file first.");
      return;
    }

    if (fileOriginalContent === fileContent) {
      setDiffText("");
      setDiffError("No changes detected between loaded file and editor content.");
      setDiffLineCount(0);
      return;
    }

    setDiffLoading(true);
    setDiffError(null);
    setDiffText("");
    setDiffLineCount(null);

    const body = {
      original_content: fileOriginalContent,
      updated_content: fileContent,
      file_id: selectedFileId,
      file_path: selectedFileMeta?.path || null,
      change_summary: changeSummary || "",
      username: currentUsername || "unknown",
      change_id: activeChange && activeChange.id ? activeChange.id : null,

      // NEW: project_id (backend can ignore if not needed)
      project_id: selectedProjectId || null,
    };

    try {
      const res = await fetch("/api/dev-ide/preview-diff", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to generate diff.");
      }

      setDiffText(data.diff || "");
      setDiffLineCount(
        typeof data.line_count_changed === "number"
          ? data.line_count_changed
          : null
      );
    } catch (err) {
      setDiffError(err.message);
    } finally {
      setDiffLoading(false);
    }
  };

  const handleSaveFile = async () => {
    if (!selectedFileId || !selectedFileMeta) return;

    if (!changeSummary || !changeSummary.trim()) {
      setSaveMessage("Please enter a short change summary before saving.");
      return;
    }

    setSaveLoading(true);
    setSaveMessage(null);
    setFileError(null);

    const body = {
      file_id: selectedFileId,
      original_content: fileOriginalContent,
      updated_content: fileContent,
      change_summary: changeSummary.trim(),
      username: currentUsername || "unknown",
      change_id: activeChange && activeChange.id ? activeChange.id : null,

      // NEW: project_id for backend root enforcement
      project_id: selectedProjectId || null,
    };

    try {
      const res = await fetch("/api/dev-ide/save-file", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to save file.");
      }

      setFileOriginalContent(fileContent);
      setDiffText("");
      setDiffError(null);
      setDiffLineCount(0);

      const result = data.result || {};
      const ratioPercent =
        typeof result.change_ratio === "number"
          ? ` (changed ~${Math.round(result.change_ratio * 100)}% of lines)`
          : "";

      setSaveMessage(
        `Saved successfully. Backup: ${result.backup_file || "n/a"}${ratioPercent}`
      );

      if (result.had_errors) {
        const softMsg =
          result.error_message ||
          "One or more issues were detected while saving. Check the Error Log for details.";
        if (typeof setSaveErrorMessage === "function") {
          setSaveErrorMessage(softMsg);
        }
        if (typeof setSaveErrorOpen === "function") {
          setSaveErrorOpen(true);
        }
      }

      if (activeChange && activeChange.id) {
        await loadChangeFiles(activeChange.id);
        await loadSqlFiles(activeChange.id);
      }
    } catch (err) {
      setFileError(err.message);
      if (typeof setSaveErrorMessage === "function") {
        setSaveErrorMessage(`Error saving file: ${err.message}`);
      }
      if (typeof setSaveErrorOpen === "function") {
        setSaveErrorOpen(true);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  return {
    handleSelectFile,
    handleCopyFullForGPT,
    handleCopySelectionForGPT,
    handleApplyGptPaste,
    handlePreviewDiff,
    handleSaveFile,
  };
};

export default createFileEditorHandlers;
